import React, { useState, useEffect, useCallback } from 'react';
import Topbar from '../components/Topbar';
import Modal from '../components/Modal';
import api, { API } from '../utils/api';
import { MagnifyingGlass, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import usePullToRefresh from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';
import useContentHeight from '../hooks/useContentHeight';

const Productos = () => {
  const contentHeight = useContentHeight(280);
  const [productos, setProductos] = useState([]);
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [dominios, setDominios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProducto, setEditingProducto] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('todos');
  const [useNewName, setUseNewName] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre: '',
    categoria: 'Hosting',
    dominio_id: '',
    precio_base: 0,
    precio_iva: 0,
    periodicidad: 'mensual',
    fecha_registro: '',
    fecha_vencimiento: '',
    fecha_pago: '',
    estado: '',
    descripcion: ''
  });

  const [includeIva, setIncludeIva] = useState(false);

  // Función para calcular precio + IVA (16%)
  const calcularPrecioIva = (precio) => {
    return parseFloat((precio * 1.16).toFixed(2));
  };

  // Función para calcular el porcentaje de tiempo restante
  const getExpiryPercentage = (fechaVencimiento) => {
    if (!fechaVencimiento) return 0;
    const today = new Date();
    const expiry = new Date(fechaVencimiento);
    const oneYearAgo = new Date(expiry);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    const totalDays = (expiry - oneYearAgo) / (1000 * 60 * 60 * 24);
    const daysLeft = (expiry - today) / (1000 * 60 * 60 * 24);
    
    return Math.max(0, Math.min(100, (daysLeft / totalDays) * 100));
  };

  // Función para obtener el color de la barra según el porcentaje
  const getExpiryColor = (percentage) => {
    if (percentage > 50) return '#00d68f';
    if (percentage > 25) return '#ffc84a';
    return '#ff5252';
  };

  // Categorías de productos
  const productosCategories = ['Automatización', 'Chat', 'Email', 'Hosting', 'Solución Web'];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterProductos();
  }, [productos, searchTerm, filter]);

  const fetchData = async () => {
    try {
      const [productosRes, dominiosRes] = await Promise.all([
        api.get('/productos'),
        api.get('/dominios')
      ]);
      // Filtrar solo productos
      const prods = productosRes.data.filter(p => productosCategories.includes(p.categoria));
      setProductos(prods);
      setDominios(dominiosRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const filterProductos = () => {
    let filtered = [...productos];

    // Filtrar según selección
    if (filter === 'pagados') {
      // Mostrar SOLO productos con estado "pagado"
      filtered = filtered.filter(p => p.estado === 'pagado');
    } else {
      // "todos" - Ocultar productos con estado "pagado"
      filtered = filtered.filter(p => p.estado !== 'pagado');
    }

    if (searchTerm) {
      filtered = filtered.filter(p => {
        const dominio = dominios.find(d => d.id === p.dominio_id);
        const dominioNombre = dominio?.nombre || '';
        return p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
               p.categoria.toLowerCase().includes(searchTerm.toLowerCase()) ||
               dominioNombre.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    setFilteredProductos(filtered);
  };

  // Pull to refresh for mobile
  const handleRefresh = useCallback(async () => {
    await fetchData();
    toast.success('Datos actualizados');
  }, []);

  const {
    containerRef,
    pullDistance,
    pullProgress,
    isRefreshing
  } = usePullToRefresh(handleRefresh);

  const handleAdd = () => {
    setEditingProducto(null);
    setUseNewName(true);
    setIncludeIva(false);
    setFormData({
      nombre: '',
      categoria: 'Hosting',
      dominio_id: '',
      precio_base: 0,
      precio_iva: 0,
      periodicidad: 'anual',
      fecha_registro: '',
      fecha_vencimiento: '',
      fecha_pago: '',
      estado: '',
      descripcion: ''
    });
    setShowModal(true);
  };

  const handleEdit = (producto) => {
    setEditingProducto(producto);
    setUseNewName(false);
    setIncludeIva(producto.precio_iva > 0);
    setFormData({
      nombre: producto.nombre || '',
      categoria: producto.categoria || 'Hosting',
      dominio_id: producto.dominio_id || '',
      precio_base: producto.precio_base || 0,
      precio_iva: producto.precio_iva || 0,
      periodicidad: producto.periodicidad || 'mensual',
      fecha_registro: producto.fecha_registro || '',
      fecha_vencimiento: producto.fecha_vencimiento || '',
      fecha_pago: producto.fecha_pago || '',
      estado: producto.estado || '',
      descripcion: producto.descripcion || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (productoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;

    try {
      await api.delete(`/productos/${productoId}`);
      toast.success('Producto eliminado exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const dataToSend = {
        ...formData,
        precio_iva: includeIva ? calcularPrecioIva(formData.precio_base) : 0
      };
      
      if (editingProducto) {
        await api.put(`/productos/${editingProducto.id}`, dataToSend);
        toast.success('Producto actualizado exitosamente');
      } else {
        await api.post('/productos', dataToSend);
        toast.success('Producto creado exitosamente');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Error al guardar producto');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      window.open(`${API}/export/productos?token=${token}`, '_blank');
      toast.success('Exportando productos...');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const getCategoryColor = (categoria) => {
    const colors = {
      'Automatización': 'bg-[#b06eff]/10 border-[#b06eff] text-[#b06eff]',
      'Chat': 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]',
      'Email': 'bg-[#4f9eff]/10 border-[#4f9eff] text-[#4f9eff]',
      'Hosting': 'bg-[#4f9eff]/10 border-[#4f9eff] text-[#4f9eff]',
      'Solución Web': 'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]',
    };
    return colors[categoria] || 'bg-[#8b949e]/10 border-[#8b949e] text-[#8b949e]';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0d0f14]">
        <div className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-lg">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-[#0d0f14] min-h-screen" ref={containerRef}>
      <Topbar
        title="Hosting"
        breadcrumb="Visionweb / Módulos / Hosting"
        onAdd={handleAdd}
        onExport={handleExport}
        showAdd={true}
        showExport={true}
      />

      <div className="p-4 md:p-6 pb-12">
        {/* Pull to Refresh Indicator - Mobile only */}
        <div className="block md:hidden">
          <PullToRefreshIndicator 
            pullDistance={pullDistance}
            pullProgress={pullProgress}
            isRefreshing={isRefreshing}
          />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b949e]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar productos..."
              className="w-full bg-[#13161e] border border-[#2a2e3d] rounded-sm pl-10 pr-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
            {['todos', 'pagados'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 md:px-4 py-2 rounded-sm font-['IBM_Plex_Mono'] text-xs whitespace-nowrap transition-colors ${
                  filter === f
                    ? 'bg-[#4f9eff] text-[#0d0f14] font-bold'
                    : 'bg-[#13161e] border border-[#2a2e3d] text-[#8b949e] hover:text-white'
                }`}
                data-testid={`filter-${f}`}
              >
                {f === 'pagados' ? 'Pagados' : 'Todos'}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Cards View */}
        <div className="block md:hidden">
          <div className="overflow-y-auto space-y-3 pr-1" style={{ maxHeight: contentHeight }}>
            {filteredProductos.map((producto, index) => {
              const dominio = dominios.find(d => d.id === producto.dominio_id);
              const percentage = getExpiryPercentage(producto.fecha_vencimiento);
              const color = getExpiryColor(percentage);
              return (
                <div key={`${producto.id}-${producto.fecha_vencimiento}-${index}`} className="bg-[#13161e] border border-[#2a2e3d] rounded-md p-4" data-testid={`producto-card-${producto.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-sm font-semibold truncate">{dominio?.nombre || '-'}</div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 border rounded-sm font-['IBM_Plex_Mono'] text-[10px] uppercase ml-2 ${
                      producto.estado === 'vencido' 
                        ? 'bg-[#ff5252]/10 border-[#ff5252] text-[#ff5252]' 
                        : producto.estado === 'por vencer'
                        ? 'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]'
                        : 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]'
                    }`}>
                      {producto.estado || 'vencido'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                    <div>
                      <span className="text-[#8b949e]">Precio: </span>
                      <span className="text-white font-['IBM_Plex_Mono'] font-bold">${producto.precio_base.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-[#8b949e]">+IVA: </span>
                      <span className="text-[#00d68f] font-['IBM_Plex_Mono']">{producto.precio_iva > 0 ? `$${producto.precio_iva.toFixed(2)}` : '-'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs">{producto.fecha_vencimiento || '-'}</span>
                    <div className="flex-1 h-1 bg-[#2a2e3d] rounded-full overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${percentage}%`, backgroundColor: color }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-[#2a2e3d]">
                    <button
                      onClick={() => handleEdit(producto)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#4f9eff]/10 border border-[#4f9eff] text-[#4f9eff] rounded-sm text-xs font-['IBM_Plex_Mono']"
                      data-testid={`edit-producto-mobile-${producto.id}`}
                    >
                      <PencilSimple size={14} /> Editar
                    </button>
                    <button
                      onClick={() => handleDelete(producto.id)}
                      className="px-3 py-2 bg-[#ff5252]/10 border border-[#ff5252] text-[#ff5252] rounded-sm"
                      data-testid={`delete-producto-mobile-${producto.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-[#13161e] border border-[#2a2e3d] rounded-md overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
            <table className="w-full">
              <thead className="bg-[#0d0f14] border-b border-[#2a2e3d] sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Dominio</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Precio</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">+ IVA</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">Vencimiento</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">Pago</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2a2e3d]">
                {filteredProductos.map((producto, index) => {
                  const dominio = dominios.find(d => d.id === producto.dominio_id);
                  const percentage = getExpiryPercentage(producto.fecha_vencimiento);
                  const color = getExpiryColor(percentage);
                  return (
                    <tr key={`${producto.id}-${producto.fecha_vencimiento}-${index}`} className="hover:bg-[#1a1e28] transition-colors group" data-testid={`producto-row-${producto.id}`}>
                      <td className="px-6 py-4 text-[#8b949e] font-['IBM_Plex_Mono'] text-sm">
                        {dominio?.nombre || '-'}
                      </td>
                      <td className="px-4 py-4 text-white font-['IBM_Plex_Mono'] text-sm font-bold">
                        ${producto.precio_base.toFixed(2)}
                      </td>
                      <td className="px-4 py-4 text-[#00d68f] font-['IBM_Plex_Mono'] text-sm">
                        {producto.precio_iva > 0 ? `$${producto.precio_iva.toFixed(2)}` : '-'}
                      </td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <div className="text-white font-['IBM_Plex_Mono'] text-sm whitespace-nowrap">
                            {producto.fecha_vencimiento || '-'}
                          </div>
                          {producto.fecha_vencimiento && (
                            <div className="h-1 w-20 bg-[#2a2e3d] rounded-full overflow-hidden">
                              <div 
                                className="h-full transition-all" 
                                style={{ width: `${percentage}%`, backgroundColor: color }}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-[#4f9eff] font-['IBM_Plex_Mono'] text-sm whitespace-nowrap">
                        {producto.fecha_pago || '-'}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2 py-1 border rounded-sm font-['IBM_Plex_Mono'] text-xs uppercase ${
                          producto.estado === 'vencido' 
                            ? 'bg-[#ff5252]/10 border-[#ff5252] text-[#ff5252]' 
                            : producto.estado === 'por vencer'
                            ? 'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]'
                            : 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]'
                        }`}>
                          {producto.estado || 'vencido'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(producto)}
                            className="text-[#4f9eff] hover:text-white transition-colors"
                            data-testid={`edit-producto-${producto.id}`}
                          >
                            <PencilSimple size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(producto.id)}
                            className="text-[#ff5252] hover:text-white transition-colors"
                            data-testid={`delete-producto-${producto.id}`}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {filteredProductos.length === 0 && (
          <div className="text-center py-12">
            <div className="text-[#8b949e] font-['Mulish'] text-sm">No se encontraron productos</div>
          </div>
        )}
      </div>

      {/* Modal Form */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingProducto ? 'Editar Hosting' : 'Agregar Hosting'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Fila 1: Nombre | Precio */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Nombre *</label>
                <button
                  type="button"
                  onClick={() => {
                    setUseNewName(!useNewName);
                    if (!useNewName) setFormData({ ...formData, nombre: '' });
                  }}
                  className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-xs hover:underline"
                >
                  {useNewName ? 'Seleccionar existente' : 'Agregar nuevo'}
                </button>
              </div>
              {useNewName ? (
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                  placeholder="Escribir nuevo nombre..."
                  required
                  data-testid="form-nombre-input"
                />
              ) : (
                <select
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                  required
                  data-testid="form-nombre-select"
                >
                  <option value="">Seleccionar producto</option>
                  {[...new Set(productos.map(p => p.nombre))].sort((a, b) => a.localeCompare(b)).map(nombre => (
                    <option key={nombre} value={nombre}>{nombre}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Precio *</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={formData.precio_base}
                  onChange={(e) => setFormData({ ...formData, precio_base: parseFloat(e.target.value) || 0 })}
                  className="flex-1 min-w-0 bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                  required
                  data-testid="form-precio"
                />
                <button
                  type="button"
                  onClick={() => setIncludeIva(!includeIva)}
                  className={`px-3 py-2 rounded-sm font-['IBM_Plex_Mono'] text-xs transition-colors whitespace-nowrap ${
                    includeIva
                      ? 'bg-[#00d68f] text-[#0d0f14] font-bold'
                      : 'bg-[#13161e] border border-[#2a2e3d] text-[#8b949e] hover:text-white hover:border-[#00d68f]'
                  }`}
                  data-testid="btn-iva"
                >
                  +IVA
                </button>
              </div>
              {includeIva && formData.precio_base > 0 && (
                <p className="text-[#00d68f] text-xs mt-1 font-['IBM_Plex_Mono']">
                  Con IVA (16%): ${calcularPrecioIva(formData.precio_base).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {/* Fila 2: Categoría | Periodo */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Categoría *</label>
              <select
                value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-categoria"
              >
                <option value="Automatización">Automatización</option>
                <option value="Chat">Chat</option>
                <option value="Email">Email</option>
                <option value="Hosting">Hosting</option>
                <option value="Solución Web">Solución Web</option>
              </select>
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Periodo *</label>
              <select
                value={formData.periodicidad}
                onChange={(e) => setFormData({ ...formData, periodicidad: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-periodicidad"
              >
                <>
                  <option value="mensual">Mensual</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="semestral">Semestral</option>
                  <option value="anual">Anual</option>
                  <option value="2años">2 Años</option>
                  <option value="3años">3 Años</option>
                  <option value="proyecto">Proyecto</option>
                </>
              </select>
            </div>
          </div>

          {/* Fila 3: Dominio | Estado */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Dominio</label>
              <select
                value={formData.dominio_id}
                onChange={(e) => setFormData({ ...formData, dominio_id: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-dominio"
              >
                <option value="">Seleccionar dominio</option>
                {[...dominios].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(dominio => (
                  <option key={dominio.id} value={dominio.id}>{dominio.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Estado</label>
              <select
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-estado"
              >
                <option value="">Automático</option>
                <option value="activo">Activo</option>
                <option value="por vencer">Por Vencer</option>
                <option value="vencido">Vencido</option>
                <option value="pagado">Pagado</option>
              </select>
            </div>
          </div>

          {/* Fila 4: Fecha Registro | Fecha Pago */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Fecha Registro</label>
              <input
                type="date"
                value={formData.fecha_registro}
                onChange={(e) => setFormData({ ...formData, fecha_registro: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-fecha-registro"
              />
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Fecha Pago</label>
              <input
                type="date"
                value={formData.fecha_pago}
                onChange={(e) => setFormData({ ...formData, fecha_pago: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-fecha-pago"
              />
            </div>
          </div>

          <div>
            <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Descripción</label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
              rows="3"
              data-testid="form-descripcion"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 bg-transparent border border-[#2a2e3d] text-white rounded-sm hover:border-[#4f9eff] transition-colors font-['IBM_Plex_Mono'] text-sm"
              data-testid="form-cancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#4f9eff] text-[#0d0f14] rounded-sm hover:bg-[#4f9eff]/90 transition-colors font-['IBM_Plex_Mono'] text-sm font-bold"
              data-testid="form-submit"
            >
              Guardar
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Productos;